import React, { useCallback, useEffect, useState } from "react";
import {
  AppDatePicker,
  AppDeleteModal,
  AppFileUploader,
  AppMultiSelect,
  AppNoInfo,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from "@/layout/AppComponents";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Formik } from "formik";
import * as Yup from "yup";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";
import { axiosPath } from "../../../../../../../routing/helpers/router";
import Translator from "../../../../../../components/helpers/Translator";
import { toFormData } from "../../../../../../../minions/forms";
import errorHandler from "@/layout/components/error/ErrorHandler";
import colors from "../../../../../../../constants/colors";
import SpinnerLoader from "../../../../../../../layout/components/helpers/SpinnerLoader";
import minions from "../../../../../../../minions/minions";
import AppTextEditor from "../../../../../../components/form/Texts/AppTextEditor";
import useIsMounted from "../../../../../../components/hooks/useIsMounted";

export default function CorrectiveActionsTaskUpdateEdit(props) {
  const {
    taskID,
    selectedTaskUpdate,
    modulePermissions,
    onEdit,
    onDelete,
    onCancel,
  } = props;
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const level1 = useSelector((state) => state.currentPage?.levels?.level1);
  const [isLoading, setIsLoading] = useState(true);
  const [typeOptions, setTypeOptions] = useState();
  const [responsibleOptions, setResponsibleOptions] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // < GET LISTS
  const getEditLists = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_correctiveActions_taskUpdate_getEditLists"
          ),
          {
            moduleBridgeHex,
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setTypeOptions(r.data.typeOptions);
            setResponsibleOptions(r.data.responsibleOptions);
            setIsLoading(false);
          }
        }),
    [moduleBridgeHex]
  );

  useEffect(() => {
    getEditLists();
  }, []);
  // > GET LISTS

  // < EDIT
  const handleEdit = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_correctiveActions_taskUpdate_edit"),
          toFormData({
            ...payload,
            ...{
              moduleBridgeHex,
              taskUpdateID: selectedTaskUpdate?.id,
            },
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then((r) => {
          if (isMounted.current) onEdit();
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, selectedTaskUpdate]
  );
  // > EDIT

  // < DELETE
  const handleDelete = useCallback(
    async () =>
      axios
        .delete(
          axiosPath("api_r_user_module_correctiveActions_taskUpdate_delete"),
          {
            data: {
              moduleBridgeHex,
              taskUpdateID: selectedTaskUpdate?.id,
            },
          }
        )
        .then(() => {
          if (isMounted.current) onDelete();
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, selectedTaskUpdate]
  );
  // > DELETE

  const validationSchema = Yup.object().shape({
    dateStamp: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="module-legalCompliance" name="must.select.date" />)
    ),
    type: Yup.mixed().required(
      ReactDOMServer.renderToString(<Translator file="module-legalCompliance" name="must.select.origin" />)
    ),
    description: Yup.string().required(
      ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)
    ),
  });

  if (isLoading)
    return (
      <AppNoInfo color={colors.primaryLight}>
        <SpinnerLoader size={30} />
      </AppNoInfo>
    );

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={{
          dateStamp: selectedTaskUpdate?.date,
          limitDate: selectedTaskUpdate?.limitDate,
          type: selectedTaskUpdate?.type?.id,
          files: [],
          deletedFiles: [],
          employees: selectedTaskUpdate?.responsibles?.map((r) => r?.id),
          description: selectedTaskUpdate?.description,
        }}
        onSubmit={handleEdit}
      >
        {({ setFieldValue, handleSubmit, values, isSubmitting, errors }) => (
          <form onSubmit={handleSubmit}>
            <div className="row-no-margin">
              <div className="row">
                <div className="col-lg-12">
                  <h3>
                    {ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="edit.action.code"
                        variables={{ code: level1[taskID]?.code }}
                      />
                    )}
                  </h3>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-6">
                  <AppDatePicker
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="creation.date"
                      />
                    )}
                    value={values.dateStamp}
                    onChange={(opt) => setFieldValue("dateStamp", opt)}
                    placeholder="dd/mm/yyyy"
                    error={errors.dateStamp}
                    showMonthDropdown
                    showYearDropdown
                    required
                  />
                </div>
                {modulePermissions?.correctiveActions
                  ?.taskUpdateClosedSystem && (
                  <div className="col-lg-6">
                    <AppDatePicker
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="module.lc.limit.date"
                        />
                      )}
                      value={values.limitDate}
                      onChange={(opt) => setFieldValue("limitDate", opt)}
                      placeholder="dd/mm/yyyy"
                      error={errors.limitDate}
                      showMonthDropdown
                      showYearDropdown
                      isClearable
                    />
                  </div>
                )}
                <div className="col-lg-6">
                  {typeOptions && (
                    <AppSingleSelect
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-correctiveActions"
                          name="module.ca.task-type-2"
                        />
                      )}
                      icon={<AppRemixIcon icon="focus" />}
                      value={values.type}
                      options={typeOptions}
                      onChange={(v) => setFieldValue("type", v)}
                      error={errors.type}
                      required
                    />
                  )}
                </div>
                {modulePermissions?.correctiveActions
                  ?.taskUpdateResponsibles && (
                  <div className="col-lg-6">
                    <AppMultiSelect
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="responsibles"
                        />
                      )}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator
                          file="module-correctiveActions"
                          name="module.ca.filters.employee-select"
                        />
                      )}
                      icon={<AppRemixIcon icon="user" />}
                      value={values.employees}
                      options={responsibleOptions}
                      onChange={(v) => setFieldValue("employees", v)}
                      isLoading={!responsibleOptions}
                      hasSearchBox
                      hasSelectOptions
                    />
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-lg-12">
                  <AppTextArea
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="comment"
                      />
                    )}
                    height="195px"
                    isReadOnly
                    value={
                      <AppTextEditor
                        value={values.description}
                        onChange={(opt) => setFieldValue("description", opt)}
                        toolbar={{
                          toolbar: [["bold", "italic"]],
                        }}
                        height={138}
                      />
                    }
                    error={errors.description}
                    required
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-6">
                  <AppFileUploader
                    placeholder={
                      values.files.length === 0 ? (
                        <Translator file="general" name="select.files" />
                      ) : (
                        <Translator
                          file="admin"
                          name="general.elements-selected-elements"
                          variables={{ elements: values.files.length }}
                        />
                      )
                    }
                    onChange={(opt) => setFieldValue("files", opt)}
                    btnText={<Translator file="general" name="search..." />}
                    isMulti
                  />
                </div>
                <div className="col-lg-6">
                  <EvidenceFileList className="mb-2">
                    {selectedTaskUpdate?.files.map(
                      (f) =>
                        !values.deletedFiles.includes(f.id) && (
                          <li
                            key={f.id}
                            title={ReactDOMServer.renderToString(
                              <Translator
                                file="module-legalCompliance"
                                name="click.delete"
                              />
                            )}
                            onClick={() =>
                              setFieldValue("deletedFiles", [
                                ...values.deletedFiles,
                                f.id,
                              ])
                            }
                          >
                            <AppRemixIcon
                              className="pointer mr-3"
                              icon="delete-bin"
                              color={colors.danger}
                            />
                            {minions.files.fileIcon(f.name, true, null, "mr-2")}
                            {f.name}
                          </li>
                        )
                    )}
                  </EvidenceFileList>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12">
                  <span
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    <Translator file="user" name="user.remove" />
                  </span>
                  <AppDeleteModal
                    show={showDeleteModal}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="delete.task.update"
                      />
                    )}
                    onConfirm={handleDelete}
                    onClose={() => setShowDeleteModal(false)}
                  />
                  <div className="float-right">
                    <button
                      type="button"
                      className="btn btn-outline-light btn-sm mr-2"
                      onClick={onCancel}
                      disabled={isSubmitting}
                    >
                      <Translator file="user" name="user.cancel" />
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn btn-success btn-sm"
                    >
                      <Translator file="general" name="general.edit" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
}

const EvidenceFileList = styled.ul`
  list-style: none;
  margin: 10px 0 0 0;
  padding-left: 6px;
  background-color: ${colors.grey200};
  li {
    cursor: pointer;
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;
